import { useCallback, useMemo } from "react";
import { useDrop, DropTargetMonitor, useDragLayer, DragLayerMonitor } from "react-dnd";
import { IFlight } from "@vmosolutions/oc-interfaces";
import { Box, Grid, Theme } from "@mui/material";
import { DeletableFlightChip, updateRemove } from "src/components/Flights/DeletableFlightChip";
import { IFlightChip } from "src/components/Flights/FlightChip";
import { IContentForm } from "./ManageContentWindow";
import _ from "lodash";
import classNames from "classnames";

interface IFlightDropTargetProps {
  formProps: IContentForm;
  //associatedFlights: IFlight[];
  children?: any;
  disabled?: boolean;
}

export const FlightDropTarget = (props: IFlightDropTargetProps): JSX.Element => {
  // Set pending position flight.
  const { formProps, disabled } = props;

  const flightsOnDropTarget = useMemo(
    () => formProps?.values?.associatedFlights as IFlight[],
    [formProps?.values?.associatedFlights]
  );

  /**
   * Calls remove function from deletable flight and sets returned array as contentFlights
   * @param flightId - id to search content flights
   * @param contentFlights - array of flights
   */
  const callRemove = useCallback(
    (flightId: string): void => {
      formProps.setFieldValue("associatedFlights", updateRemove(flightId, flightsOnDropTarget));
      formProps.setFieldTouched("associatedFlights", true);
    },
    [flightsOnDropTarget, formProps]
  );

  // Monitor item being dragged, return as valid if type matches accepted.
  const { isValidDraggable } = useDragLayer((monitor: DragLayerMonitor) => {
    const draggedItem: any = monitor.getItem();
    return {
      isValidDraggable:
        monitor.isDragging() &&
        (draggedItem?.type === "FLIGHT" || draggedItem?.type === "SELECTED_FLIGHTS") &&
        !disabled,
    };
  });

  // HANDLE FLIGHTS if dragged over eventgroups
  const [{ isOver }, drop] = useDrop({
    //WHY! I seem to need to list "SELECTED_GROUNDEVENTS" as accepted
    //to be able to drop flights
    accept: ["FLIGHT", "SELECTED_FLIGHTS"],
    drop(item: any, monitor: DropTargetMonitor) {
      let newFlights: IFlight[] = [];

      switch (item.type) {
        case "FLIGHT":
          newFlights = [item.flight];
          break;
        case "SELECTED_FLIGHTS":
          newFlights = item.selectedFlights;
          break;
        case "SELECTED_GROUNDEVENTS":
          //left it using selectedFlights because I wasn't sure if changing the timeine
          //drag layer would cause issues else where in the app
          //newGroundEvents = item.selectedFlights;
          break;
        default:
        // code block
      }
      if (flightsOnDropTarget?.length === 0 || flightsOnDropTarget === null) {
        formProps.setFieldValue("associatedFlights", newFlights);
        formProps.setFieldTouched("associatedFlights", true);
      } else {
        const addedFlights = [...flightsOnDropTarget, ...newFlights];
        const uniqueFlight = _.uniqBy(addedFlights, _.iteratee("_id"));
        formProps.setFieldValue("associatedFlights", uniqueFlight);
        formProps.setFieldTouched("associatedFlights", true);
      }
    },

    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });
  const DropZoneStyles = (theme: Theme): any => ({
    event_dropzone: {
      display: "flex",
      cursor: "pointer",
      minHeight: "100px",
      justifyContent: "center",
      alignItems: "flex-start",
      flexDirection: "column",
      flexGrow: 1,
      backgroundColor: theme.palette.background.paper,
      borderRadius: "3px",
      padding: "4px",
      "& .title": { fontSize: 18, fontWeight: "bold" },
      "& .body": {
        display: "flex",
        border: `2x solid ${theme.custom.forms.inputBorder}`,
        minHeight: "40px",
        alignItems: "center",
        color: theme.custom.forms.focusedColor,
        fontSize: 12,
        fontWeight: 300,
        textAlign: "center",
        padding: "4px 8px",
        opacity: 0.5,
        userSelect: "none",
      },
      "&.disabled": {
        opacity: 0.5,
        userSelect: "none",
        pointerEvents: "none",
      },
      "&.reject": {
        backgroundcolor: theme.custom.forms.errorColor,
      },
      "&.active": {
        backgroundcolor: theme.custom.forms.focusedColor,
      },
      "&.dirty": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
      "&.over": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
    },
  });
  return (
    <Box
      className={`$  ${isValidDraggable && "active"} ${isOver && !disabled && "over"} ${disabled && "disabled"}`}
      ref={!disabled ? drop : null}
      sx={(theme) => DropZoneStyles(theme).event_dropzone}
    >
      <div className="body">
        To attach flights to this content, drag/drop them into this zone from the timeline or advanced search menu.
      </div>
      <Grid container direction="row" className={classNames("contentFlightChips")}>
        {flightsOnDropTarget?.length > 0
          ? flightsOnDropTarget.map((chipData: IFlightChip) => {
              return <DeletableFlightChip data={chipData} handleDelete={callRemove} key={`${chipData?._id}-chip`} />;
            })
          : null}
      </Grid>
    </Box>
  );
};
